import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Rights } from 'src/app/models/enums/rights';
import { ISortable } from 'src/app/models/interfaces/sortable.interface';
import { MemoryService } from 'src/app/services/memory.service';
import { AdvancedGridviewComponent } from '../../advanced-gridview/advanced-gridview.component';
import { AdvancedDataSource } from '../../advanced-gridview/datasources/advanced-datasource';
import { AdvancedGridviewModel } from '../../advanced-gridview/models/advanced-gridview-model';
import { ColumnListDefinition } from '../../advanced-gridview/models/column-list-definition';
import { MenuITemAction } from '../../advanced-gridview/models/menu-item-action';
import { RowClickEvent } from '../../advanced-gridview/models/row-click-event';
import { ICopyable } from '../../common/icopyable';
import { MenuItem } from '../../common/menu-item';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { ListItem } from '../../filters/models/list-item';
import { HelpDialogComponent } from '../../help-dialog/help-dialog.component';

@Component({
    template: '',
    standalone: false,
})
export class DefaultListComponent<T extends ICopyable<T>> {
    /**
     * Reference au composant AdvancedGridview.
     */
    @ViewChild(AdvancedGridviewComponent)
    public gridView: AdvancedGridviewComponent<T>;

    /**
     * Datasource du composant AdvancedGridview.
     */
    dataSource: AdvancedDataSource<T>;

    /**
     * Nombre d'enregistrements retournés à chaque requête liste du composant AdvancedGridview.
     */
    size = 100;

    /**
     * Modèle de la table filtrée.
     */
    filteredTableModel: AdvancedGridviewModel;

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Supprimer' d'un élémenent.
     */
    @Output()
    itemDeleted: EventEmitter<T> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Réinitialiser' d'un élémenent.
     */
    @Output()
    itemReseted: EventEmitter<T> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur sélectionne un élément.
     */
    @Output()
    itemSelected: EventEmitter<T> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Editer' d'un élémenent.
     */
    @Output()
    itemEdited: EventEmitter<T> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Dupliquer' d'un élémenent.
     */
    @Output()
    itemDuplicated: EventEmitter<T> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le menu contextuel 'Consulter' d'un élémenent.
     */
    @Output()
    itemDisplayed: EventEmitter<T> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur valide l'ordre des élémenents (boite de dialogue Détermination de l\'ordre des éléments).
     */
    @Output()
    itemsSorted: EventEmitter<ISortable[]> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur clique sur le bouton de création.
     */
    @Output()
    itemCreated: EventEmitter<void> = new EventEmitter();

    /**
     * Elements du menu contextuel du composant AdvancedGridview.
     */
    public menuItems: MenuItem<MenuITemAction>[] = [new MenuItem(MenuITemAction.Read, 'Consulter')];

    public getSortableItemsFunction: (item: ISortable) => ISortable[];

    /**
     * Constructeur.
     * @param dialog: Service pour ouvrir des boites de dialogue.
     * @param memoryService : Service pour récupérer les informations en mémoire.
     */
    constructor(
        public dialog: MatDialog,
        protected memoryService: MemoryService
    ) {}

    /**
     * Ajoute un élément au menu contextuel.
     * @param menuItemLabel : Libellé de l'élément.
     * @param menuItemAction : Action lié à l'élément.
     * @param neededRight : Droit necessaire pour avoir acces à cet élément.
     */
    protected AddMenuItem(menuItemLabel: string, menuItemAction: MenuITemAction, neededRight: Rights, condition: (item: T) => boolean = null) {
        if (neededRight === Rights.None || this.memoryService.getConnectedUser().hasRight(neededRight)) {
            this.menuItems.push(new MenuItem(menuItemAction, menuItemLabel, condition));
        }
    }

    /**
     * Affiche la boite de dialogue de confirmation de suppression.
     * @param confirmationText : Texte de confirmation.
     * @param item : Instance de l'élément à supprimer.
     */
    protected displayDeleteConfirmationDialog(confirmationText: string, item: T) {
        // Cas de la suppression --> affichage de la boite de dialogue de confirmation.
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: '400px',
            data: {
                title: 'Confirmation de suppression',
                message: confirmationText,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de confirmation
        dialogRef.afterClosed().subscribe((dialogResult) => {
            if (dialogResult) {
                // Suppression effective du client.
                this.itemDeleted.emit(item);
            }
        });
    }

    /**
     * Affiche l'aide de la page
     * @param page nom de la page
     */
    public displayHelpDialog(page: string) {
        const dialogRef = this.dialog.open(HelpDialogComponent, {
            minWidth: '400px',
            maxWidth: '800px',
            data: {
                page: page,
            },
            panelClass: 'empty-dialog',
        });
    }

    /**
     * Affiche la boite de dialogue de confirmation de réinitialisation.
     * @param confirmationText : Texte de confirmation.
     * @param item : Instance de l'élément à réinitialiser.
     */
    protected displayResetConfirmationDialog(confirmationText: string, item: T) {
        // Cas de la suppression --> affichage de la boite de dialogue de confirmation.
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            maxWidth: '400px',
            data: {
                title: 'Confirmation de réinitialisation',
                message: confirmationText,
            },
            panelClass: 'empty-dialog',
        });

        // Attente de la réponse de la boite de dialogue de confirmation
        dialogRef.afterClosed().subscribe((dialogResult) => {
            if (dialogResult) {
                // Suppression effective du client.
                this.itemReseted.emit(item);
            }
        });
    }

    /**
     * Appellé lorsque l'utilisateur clique sur une ligne de la liste.
     * @param event : Événement retourné par la liste.
     */
    public onSelection(event: RowClickEvent<T>): void {
        this.itemSelected.emit(event.context);
    }

    /**
     * Raffraichit le contenu de la liste.
     */
    public refresh() {
        if (this.gridView) {
            this.gridView.refresh();
        }
    }

    /**
     * Export le contenu de la liste.
     * @param exportFileName : Nom du fichier d'export.
     */
    public export(exportFileName: string) {
        this.dataSource.export(exportFileName);
    }

    /**
     * Spécifie les éléments du filtre d'une colonne de type liste
     * @param column : Instance de la colonne de type liste
     * @param listItem : Liste des éléments du filtre.
     */
    protected loadColumn(column: ColumnListDefinition<string>, listItem: ListItem<string>[]): void {
        if (!!column) {
            this.gridView.SetFilterItems(column, listItem);
        }
    }
}
